import React from "react";
import { renderWithMentions } from "./renderMentions";
import { VoiceMessageBody } from "./VoiceMessageBody";
import type { ChatVoice } from "../../services/rtkApi/agentApi";

interface ChatMessageProps {
  sender: string;
  senderType: "user" | "agent" | "self";
  content: string;
  timestamp: string;
  /** delivered/read indicator for the current user's own messages (✓ / ✓✓) */
  status?: "delivered" | "read";
  /** optional text shown next to the read tick (e.g. "seen by sitebot" in personal chat) */
  statusLabel?: string;
  /** present when this is a voice note — renders the player + fun summary instead of text */
  voice?: ChatVoice;
  /** how many times the current user has played this voice clip (server truth) */
  voicePlays?: number;
  voiceMaxPlays?: number;
  /** called when a fresh listen starts, so the play is recorded server-side */
  onPlayVoice?: () => void;
}

function formatTime(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function ChatMessage({
  sender,
  senderType,
  content,
  timestamp,
  status,
  statusLabel,
  voice,
  voicePlays = 0,
  voiceMaxPlays = 2,
  onPlayVoice,
}: ChatMessageProps) {
  const isSelf = senderType === "self";
  const isAgent = senderType === "agent";
  const showStatus = isSelf && !!status;

  // parent row supplies justify-start / justify-end — avoid nested flex squishing width
  return (
    <div
      className={`max-w-[75%] rounded-sm border-2 border-ink px-2 py-1.5 text-xs md:px-3 md:py-2 md:text-sm ${
        isAgent
          ? "bg-accent/20 border-accent/50"
          : isSelf
            ? "bg-sky/20 border-sky/50"
            : "bg-gray-50"
      }`}
      style={{
        boxShadow: "2px 2px 0px rgba(0,0,0,0.15)",
      }}
    >
      {!isSelf && (
        <div
          className={`text-[10px] md:text-xs font-bold mb-0.5 ${
            isAgent ? "text-accent" : "text-gray-500"
          }`}
        >
          {isAgent ? "sitebot" : sender}
        </div>
      )}
      {voice ? (
        <VoiceMessageBody
          voice={voice}
          plays={voicePlays}
          maxPlays={voiceMaxPlays}
          onPlay={onPlayVoice ?? (() => undefined)}
        />
      ) : (
        <div className="whitespace-pre-wrap break-words leading-snug md:leading-normal">
          {renderWithMentions(content)}
        </div>
      )}
      <div className="text-[9px] md:text-[10px] text-gray-400 mt-0.5 md:mt-1 flex items-center justify-end gap-1">
        {showStatus && (
          <span
            className={status === "read" ? "text-sky-600 font-bold" : "text-gray-400"}
          >
            {status === "read" ? "✓✓" : "✓"}
            {status === "read" && statusLabel ? ` ${statusLabel}` : ""}
          </span>
        )}
        <span>{formatTime(timestamp)}</span>
      </div>
    </div>
  );
}
